import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { BrandSocialIntegration } from '../../../components/BrandSocialIntegration';
import type { ISocialLinks } from '../../../redux/buzzbridge/types';
import { brandManualSocialLinkToApi, type IBrandManualSocialLinkData } from '../../../utils/brandManualSocialLinkUtils';
import {
    brandSocialLogin,
    createBrandSocialLink,
    getBrand,
    updateBrandSocialLink,
} from '../../../redux/buzzbridge/actions';
import { fetchBrandOpsInboxStatus } from '../../../redux/brandOps/actions';
import { brandOpsIsBrandAdminSelector, brandOpsInboxStatusSelector } from '../../../redux/brandOps/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { BRAND_LUXE_MUTED } from '../brandDashboardTokens';
import { ROUTES } from '../../../types/global/routes.types';
import { BrandManageGate, ManagePageHeader, ManageSurface, MANAGE_PAGE_SX, useBrandManageGate } from './manageShared';

type PreviewPlatform = 'instagram' | 'tiktok' | 'youtube';

export default function BrandManageSocialPage() {
    const dispatch = useDispatch();
    const dispatchP = useDispatchPromise();
    const { brand, brandId } = useBrandManageGate();
    const isAdmin = useSelector(brandOpsIsBrandAdminSelector);
    const inboxStatus = useSelector(brandOpsInboxStatusSelector);

    const [connectingPlatform, setConnectingPlatform] = useState<keyof ISocialLinks | null>(null);

    useEffect(() => {
        if (brandId) dispatch(fetchBrandOpsInboxStatus({ brandId }));
    }, [dispatch, brandId]);

    const igDisconnected = inboxStatus !== null && (!inboxStatus.connected || !inboxStatus.messaging_ready);
    const [savingManualPlatform, setSavingManualPlatform] = useState<keyof ISocialLinks | null>(null);
    const [connectedLinks, setConnectedLinks] = useState<Partial<ISocialLinks>>({});
    const [oauthLinked, setOauthLinked] = useState<Partial<Record<keyof ISocialLinks, boolean>>>({});

    const initialFromBrand = useMemo(() => {
        const links = (brand?.social_links || {}) as Partial<ISocialLinks>;
        return links;
    }, [brand?.social_links]);

    // Sync when brand changes
    React.useEffect(() => {
        setConnectedLinks(initialFromBrand);
    }, [initialFromBrand]);

    const handleConnecting = useCallback((id: keyof ISocialLinks) => {
        setConnectingPlatform(id);
    }, []);

    const handleDisconnect = (id: keyof ISocialLinks) => {
        setConnectedLinks(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setOauthLinked(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const handleOAuthCode = useCallback(
        (code: string, provider: string, platformId: keyof ISocialLinks) => {
            if (!brandId) {
                setConnectingPlatform(null);
                return;
            }
            dispatchP(brandSocialLogin({ brand_id: brandId, code, provider } as any))
                .then((res: any) => {
                    const link = res?.data?.data?.brand_social_link || res?.brand_social_link;
                    const username = link?.username || link?.display_name || platformId;
                    setConnectedLinks(prev => ({ ...prev, [platformId]: username }));
                    setOauthLinked(prev => ({ ...prev, [platformId]: true }));
                    dispatch(getBrand(brandId));
                })
                .catch(() => showToastMessage('Social connect failed', 'error'))
                .finally(() => setConnectingPlatform(null));
        },
        [brandId, dispatch, dispatchP],
    );

    const handleManualSave = useCallback(
        async (platformId: keyof ISocialLinks, data: IBrandManualSocialLinkData) => {
            if (!brandId) throw new Error('Brand required');
            setSavingManualPlatform(platformId);
            try {
                const apiBody = brandManualSocialLinkToApi(platformId as PreviewPlatform, data);
                await dispatchP(
                    createBrandSocialLink({
                        brandId,
                        payload: { platform: String(platformId), ...apiBody },
                    } as any),
                ).catch(async () =>
                    dispatchP(
                        updateBrandSocialLink({
                            brandId,
                            linkId: String(platformId),
                            payload: apiBody,
                        } as any),
                    ),
                );
                setConnectedLinks(prev => ({
                    ...prev,
                    [platformId]: data.username || data.url || platformId,
                }));
                dispatch(getBrand(brandId));
                showToastMessage('Social link saved', 'success');
            } finally {
                setSavingManualPlatform(null);
            }
        },
        [brandId, dispatch, dispatchP],
    );

    if (!isAdmin) return <Navigate to={ROUTES.BRAND_MANAGE} replace />;

    return (
        <BrandManageGate>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader
                    title="Social"
                    subtitle="Connect Instagram, TikTok, and YouTube for Brand Manage publishing."
                    back={ROUTES.BRAND_MANAGE}
                />
                {igDisconnected && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            p: 1.75,
                            mb: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 180, 0, 0.08)',
                            border: '1px solid rgba(255, 180, 0, 0.25)',
                        }}>
                        <WarningAmberIcon sx={{ color: '#ffb400', fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                        <Box>
                            <Typography sx={{ color: '#ffb400', fontSize: 13, fontWeight: 600, mb: 0.25 }}>
                                Instagram connection needs attention
                            </Typography>
                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                                {inboxStatus?.reason ||
                                    'Your Instagram token may have expired or permissions were revoked.'}{' '}
                                Reconnect Instagram below to restore publishing and messaging.
                            </Typography>
                        </Box>
                    </Box>
                )}
                <ManageSurface sx={{ p: { xs: 1.5, md: 2 } }}>
                    <BrandSocialIntegration
                        brandId={brandId}
                        connectedLinks={connectedLinks}
                        oauthLinkedPlatforms={oauthLinked}
                        connectingPlatform={connectingPlatform}
                        savingManualPlatform={savingManualPlatform}
                        onConnecting={handleConnecting}
                        onDisconnect={handleDisconnect}
                        onOAuthCode={handleOAuthCode}
                        onAuthFailed={() => setConnectingPlatform(null)}
                        onManualSave={handleManualSave}
                    />
                </ManageSurface>
            </Box>
        </BrandManageGate>
    );
}
